import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Luggage } from "lucide-react";
import { api, ApiError, type BagDay } from "../lib/api";
import { UploadLoads } from "./UploadLoads";

/**
 * Bags, as the local bag system reports them.
 *
 * It sends a running count per flight per point — checked in, screened, made up, loaded —
 * and nothing about any individual bag. This screen is the picture that makes: how far
 * each flight's bags have got, when the last report came in, and the flow through the day
 * from the increases between reports.
 */

const STAGES = [
  { key: "checkin", label: "Checked in" },
  { key: "screening", label: "Screened" },
  { key: "makeup", label: "Made up" },
  { key: "loaded", label: "Loaded" },
] as const;

const ARRIVAL_STAGES = [
  { key: "offloaded", label: "Offloaded" },
  { key: "reclaim", label: "On the belt" },
] as const;

const card = "rounded-xl border border-line p-4";

export function Bags({ today, tz, canUpload = false }: { today: string; tz: string; canUpload?: boolean }) {
  const [date, setDate] = useState(today);
  const q = useQuery({ queryKey: ["bags", date], queryFn: () => api.bags(date), refetchInterval: 30_000, retry: false });
  const cover = useQuery({ queryKey: ["bag-coverage"], queryFn: () => api.bagCoverage(14), retry: false });
  const d = q.data;

  const clock = (iso: string | null) => (iso ? DateTime.fromISO(iso).setZone(tz).toFormat("HH:mm") : "—");
  const ago = (iso: string | null) => {
    if (!iso) return "nothing yet";
    const m = Math.round((Date.now() - DateTime.fromISO(iso).toMillis()) / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    return clock(iso);
  };

  const notOn = q.error instanceof ApiError && q.error.status === 503;

  return (
    <div className="scroll-thin h-full overflow-auto p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="mr-2 flex items-center gap-2 text-[18px] font-bold">
          <Luggage className="size-5 text-ink-3" /> Bags
        </h1>
        {q.isFetching && <Loader2 className="size-4 animate-spin text-ink-3" />}
        <div className="ml-auto flex flex-wrap items-center gap-2 text-[12px]">
          {/* Load sheets carry booked bag counts too, so the way in is here as well as on Forecast. */}
          {canUpload && <UploadLoads compact />}
          <label className="flex items-center gap-1">
            Day <input type="date" className="rounded-md border border-line bg-panel px-2 py-1.5 text-[12.5px]" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>
      </div>

      {notOn ? (
        <p className="text-[13px] text-ink-3">Bag collection is not switched on for this server.</p>
      ) : !d ? (
        <p className="text-[13px] text-ink-3">Loading…</p>
      ) : !d.receiving ? (
        <WaitingForBags />
      ) : (
        <>
          {d.coverage && (
            <div
              className={clsx(
                card,
                "mb-4",
                d.coverage.overdue > 0 ? "border-warn/50" : d.coverage.counted === d.coverage.flights ? "border-ok/50" : "",
              )}
            >
              <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold">
                Coverage
                <span className={clsx("text-[12px] font-normal", d.coverage.overdue > 0 ? "text-warn" : "text-ink-2")}>
                  counts for {d.coverage.counted} of {d.coverage.flights} flights today
                </span>
              </div>
              {d.coverage.overdue > 0 ? (
                <>
                  <p className="mb-2 text-[12.5px] text-ink-3">
                    {d.coverage.overdue} {d.coverage.overdue === 1 ? "flight has" : "flights have"} gone without a single bag being reported. Flights still ahead of
                    their bank are not counted as missing.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {d.coverage.missing
                      .filter((f) => f.overdue)
                      .slice(0, 24)
                      .map((f) => (
                        <span key={`${f.flight}-${f.direction}`} className="rounded-full border border-warn/40 px-2 py-0.5 text-[11.5px] text-warn">
                          {f.flight} <span className="text-ink-3">{clock(new Date(f.scheduled).toISOString())}</span>
                        </span>
                      ))}
                  </div>
                </>
              ) : (
                <p className="text-[12.5px] text-ink-3">
                  Every flight whose bank has started has counts. {d.coverage.flights - d.coverage.counted > 0
                    ? `${d.coverage.flights - d.coverage.counted} still to come.`
                    : ""}
                </p>
              )}
              {!!d.coverage.unmatched?.length && (
                <div className="mt-2 rounded-lg border border-bad/40 p-2">
                  <div className="text-[12px] font-semibold text-bad">Counts with no flight to attach to</div>
                  <p className="mb-1 text-[12px] text-ink-3">
                    Bags were reported against these, and {d.date} has no such flight. Nearly always the flight number written differently at the sending end.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {d.coverage.unmatched.map((u) => (
                      <span key={u.flight} className="rounded-full border border-bad/40 px-2 py-0.5 text-[11.5px] text-bad">
                        {u.flight} · {u.bags} bags
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* The last fortnight, so a backfill after the link was down can be seen to have
              filled the hole. A day with no counts at all is the one to chase. */}
          {!!cover.data?.days.length && (
            <div className={clsx(card, "mb-4")}>
              <div className="mb-2 text-[13px] font-semibold">The last two weeks</div>
              <div className="flex flex-wrap gap-1">
                {cover.data.days.map((day) => {
                  const share = day.flights ? day.counted / day.flights : 0;
                  return (
                    <button
                      key={day.date}
                      onClick={() => setDate(day.date)}
                      title={`${day.date}: counts for ${day.counted} of ${day.flights} flights, ${day.bags.toLocaleString()} bags`}
                      className={clsx(
                        "rounded px-1.5 py-1 text-[11px] tabular",
                        day.date === date && "ring-1 ring-ink-3",
                        share === 0 ? "bg-bad/15 text-bad" : share < 0.6 ? "bg-warn/15 text-warn" : "bg-ok/15 text-ok",
                      )}
                    >
                      {day.date.slice(5)}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[12px] text-ink-3">
                Each day is the share of its flights with bag counts. Red is a day nothing arrived for — after the link has been down, that is what a backfill should
                turn green. Select a day to open it.
              </p>
            </div>
          )}

          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Tile label="Bags today" value={d.totals.bags.toLocaleString()} />
            <Tile label="Flights" value={String(d.totals.flights)} />
            {STAGES.map((s) => (
              <Tile key={s.key} label={s.label} value={(d.totals.byStage[s.key] ?? 0).toLocaleString()} />
            ))}
          </div>

          <div className={clsx(card, "mb-4")}>
            <div className="mb-2 flex items-baseline justify-between">
              <div className="text-[13px] font-semibold">Bags through the day</div>
              <div className="text-[11.5px] text-ink-3">last report {ago(d.lastAt)}</div>
            </div>
            <Flow day={d} tz={tz} />
          </div>

          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
                <th className="px-2 py-1.5">Flight</th>
                {STAGES.map((s) => (
                  <th key={s.key} className="px-2 py-1.5 text-right">
                    {s.label}
                  </th>
                ))}
                <th className="px-2 py-1.5">Progress</th>
                <th className="px-2 py-1.5 text-right">Last report</th>
              </tr>
            </thead>
            <tbody>
              {d.flights.map((f) => {
                const stages = f.direction === "ARR" ? ARRIVAL_STAGES : STAGES;
                const top = Math.max(1, ...Object.values(f.stages).map((s) => s.bags));
                const last = Object.values(f.stages)
                  .map((s) => s.at)
                  .filter(Boolean)
                  .sort()
                  .at(-1) as string | null;
                return (
                  <tr key={f.flight} className="border-b border-line">
                    <td className="px-2 py-1.5 font-semibold">
                      {f.flight}
                      <span className="ml-1.5 text-[11px] font-normal text-ink-3">{f.direction}</span>
                    </td>
                    {STAGES.map((s) => {
                      const own = stages.find((x) => x.key === s.key);
                      const v = own ? f.stages[s.key] : undefined;
                      return (
                        <td key={s.key} className={clsx("tabular px-2 py-1.5 text-right", v ? "" : "text-ink-3")}>
                          {v ? v.bags.toLocaleString() : own ? "—" : ""}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5">
                      <div className="flex h-2 w-40 overflow-hidden rounded bg-panel-2">
                        {stages.map((s, i) => (
                          <div
                            key={s.key}
                            title={`${s.label}: ${f.stages[s.key]?.bags ?? 0}`}
                            className={clsx("h-full", ["bg-ink-3", "bg-info", "bg-warn", "bg-ok"][i] ?? "bg-ok")}
                            style={{ width: `${((f.stages[s.key]?.bags ?? 0) / top / stages.length) * 100}%` }}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="tabular px-2 py-1.5 text-right text-ink-3">{ago(last)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-3 max-w-3xl text-[12px] text-ink-3">
            Counts come from the bag system on the local server, per flight and per point. No bag is described to Vectro Allocate individually, and a bag tag is never
            stored. The same counts appear beside the passenger figures on the board.
          </p>
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className={card}>
      <div className="text-[11px] tracking-wide text-ink-3 uppercase">{label}</div>
      <div className="tabular text-[20px] font-bold">{value}</div>
    </div>
  );
}

/** Bags reported per quarter hour of the day, from the increases between reports. */
function Flow({ day, tz }: { day: BagDay; tz: string }) {
  const BUCKET = 15;
  const buckets = new Map<number, number>();
  for (const f of day.flights) {
    const seen = new Map<string, number>();
    for (const r of f.reports) {
      const key = `${f.flight}|${r.stage}`;
      const before = seen.get(key) ?? 0;
      const added = Math.max(0, r.count - before);
      seen.set(key, Math.max(before, r.count));
      if (!added || r.stage !== "checkin") continue;
      const t = DateTime.fromISO(r.at).setZone(tz);
      const slot = t.hour * 60 + Math.floor(t.minute / BUCKET) * BUCKET;
      buckets.set(slot, (buckets.get(slot) ?? 0) + added);
    }
  }
  const slots = [...buckets.entries()].sort((a, b) => a[0] - b[0]);
  if (!slots.length)
    return <p className="text-[12.5px] text-ink-3">Nothing to draw yet. The flow appears once counts arrive more than once for a flight, which is what shows the rate.</p>;
  const top = Math.max(...slots.map(([, v]) => v));
  const label = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  return (
    <div>
      <div className="flex h-28 items-end gap-0.5">
        {slots.map(([slot, v]) => (
          <div key={slot} className="flex-1" title={`${label(slot)} · ${v} bags checked in`}>
            <div className="w-full rounded-t bg-info" style={{ height: `${(v / top) * 100}%`, minHeight: 2 }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-ink-3">
        <span>{label(slots[0]![0])}</span>
        <span>bags checked in per quarter hour</span>
        <span>{label(slots.at(-1)![0])}</span>
      </div>
    </div>
  );
}

function WaitingForBags() {
  return (
    <div className={clsx(card, "max-w-2xl")}>
      <div className="mb-1 text-[14px] font-bold">Nothing reported yet</div>
      <p className="mb-3 text-[12.5px] text-ink-3">
        This screen fills in as the bag system on the local server reports counts. It needs an API key with the <b>bags:write</b> scope, made in Settings →
        Integrations &amp; API.
      </p>
      <div className="mb-2 text-[12px] font-semibold">What it sends</div>
      <pre className="scroll-thin overflow-auto rounded-lg bg-panel-2 p-3 text-[11.5px] leading-relaxed">{`POST /api/v1/bags/counts
Authorization: Bearer <key>

{
  "airport": "CYLW",
  "counts": [
    { "flight": "WS181", "flightDate": "2026-09-26", "direction": "DEP",
      "stage": "checkin", "bags": 63, "at": "2026-09-26T13:42:00-07:00" }
  ]
}`}</pre>
      <p className="mt-3 text-[12.5px] text-ink-3">
        Send the running total for a flight and point whenever it changes — <b>checkin</b>, <b>screening</b>, <b>makeup</b>, <b>loaded</b>, and for arrivals{" "}
        <b>offloaded</b> and <b>reclaim</b>. The increases between reports give the flow, so no bag ever has to be described individually. The full contract is at{" "}
        <b>/api/docs</b>.
      </p>
    </div>
  );
}
